import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Paper,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  Mic,
  Stop,
  PlayArrow,
  Pause,
  Delete,
} from '@mui/icons-material';

interface AudioRecorderProps {
  onRecordingComplete: (audioBlob: Blob) => void;
  disabled?: boolean;
  compact?: boolean;
  resetTrigger?: number; // When this changes, reset the recorder state
  label?: string; // Override the default button label
}

export const AudioRecorder = ({ onRecordingComplete, disabled = false, compact = false, resetTrigger, label }: AudioRecorderProps) => {
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const prevResetTriggerRef = useRef(resetTrigger);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, [audioUrl]);

  // Reset audio state when resetTrigger changes
  useEffect(() => {
    if (resetTrigger !== undefined && resetTrigger !== prevResetTriggerRef.current) {
      prevResetTriggerRef.current = resetTrigger;
      
      // Clean up any existing recording
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      // Reset all state
      setIsRecording(false);
      setIsPaused(false);
      setRecordingTime(0);
      setAudioBlob(null);
      setAudioUrl(null);
      setIsPlaying(false);
      setError(null);
      audioChunksRef.current = [];
    }
  }, [resetTrigger, audioUrl]);

  const startRecording = async () => {
    try {
      setError(null);

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Trigger GPU warm-up (fire and forget)
      try {
        // We use fetch to avoid interceptors handling errors if it fails
        // but apiClient is also fine. Let's use a direct fetch or apiClient.
        // Actually, let's use apiClient but catch errors silently.
        // We need to import apiClient at the top.
        // Since we can't easily add imports with replace_file_content if not contiguous, 
        // I will assume I can edit the import block separately or just use fetch with a relative path.
        // Safest is to use fetch with relative path if we know the proxy setup.
        // But let's use the window.fetch to keep it simple and import-free for this block.
        // Assuming /api/v1/health/warmup is the path.
        fetch('/api/v1/health/warmup', { method: 'POST' }).catch(console.error);
      } catch (e) {
        // Ignore warmup errors
      }

      // Create MediaRecorder with explicit low bitrate to stay well under Cloud Run's 32 MB
      // request body limit. 32 kbps Opus is more than sufficient for speech quality.
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 32000,
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      // Handle data available
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      // Handle recording stopped
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(audioBlob);

        setAudioBlob(audioBlob);
        setAudioUrl(url);

        // Stop all tracks to release microphone
        stream.getTracks().forEach((track) => track.stop());

        // Clear timer
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
      };

      // Start recording
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Error starting recording:', err);
      setError(t('errors.errorMicrophoneAccess'));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      timerIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    }
  };

  const deleteRecording = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  const playAudio = () => {
    if (audioRef.current && audioUrl) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleUseRecording = () => {
    if (audioBlob) {
      onRecordingComplete(audioBlob);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Paper elevation={compact ? 0 : 2} sx={{ p: compact ? 1 : 3, border: compact ? '1px solid' : 'none', borderColor: 'divider' }}>
      {!compact && (
        <Typography variant="h6" gutterBottom>
          Enregistrement audio
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Recording controls */}
        {!audioBlob && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {!isRecording ? (
              <Button
                variant="contained"
                color="error"
                startIcon={<Mic />}
                onClick={startRecording}
                disabled={disabled}
                size={compact ? "medium" : "large"}
                fullWidth={compact}
              >
                {label ?? (compact ? "Enregistrer" : "Commencer l'enregistrement")}
              </Button>
            ) : (
              <>
                <IconButton
                  color="error"
                  onClick={stopRecording}
                  size={compact ? "medium" : "large"}
                  sx={{ border: 2, borderColor: 'error.main' }}
                >
                  <Stop />
                </IconButton>

                {!isPaused ? (
                  <IconButton
                    color="primary"
                    onClick={pauseRecording}
                    size={compact ? "medium" : "large"}
                  >
                    <Pause />
                  </IconButton>
                ) : (
                  <IconButton
                    color="primary"
                    onClick={resumeRecording}
                    size={compact ? "medium" : "large"}
                  >
                    <PlayArrow />
                  </IconButton>
                )}

                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant={compact ? "body1" : "h5"} color="error">
                    {formatTime(recordingTime)}
                  </Typography>
                  {!isPaused && (
                    <Box sx={{ flex: 1 }}>
                      <LinearProgress color="error" />
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>
        )}

        {/* Playback controls */}
        {audioBlob && audioUrl && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {!isPlaying ? (
                <IconButton color="primary" onClick={playAudio} size={compact ? "medium" : "large"}>
                  <PlayArrow />
                </IconButton>
              ) : (
                <IconButton color="primary" onClick={pauseAudio} size={compact ? "medium" : "large"}>
                  <Pause />
                </IconButton>
              )}

              <Typography variant="body1">
                {compact ? formatTime(recordingTime) : `Durée: ${formatTime(recordingTime)}`}
              </Typography>

              <Box sx={{ flex: 1 }} />

              <IconButton color="error" onClick={deleteRecording} size={compact ? "small" : "medium"}>
                <Delete />
              </IconButton>
            </Box>

            <audio
              ref={audioRef}
              src={audioUrl}
              onEnded={() => setIsPlaying(false)}
              style={{ width: '100%', display: compact ? 'none' : 'block' }}
              controls={!compact}
            />

            <Button
              variant="contained"
              color="primary"
              onClick={handleUseRecording}
              disabled={disabled}
              fullWidth
              size={compact ? "small" : "medium"}
            >
              {compact ? "Utiliser" : "Utiliser cet enregistrement"}
            </Button>
          </Box>
        )}
      </Box>
    </Paper>
  );
};
